import { createContext, useContext, useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Flex } from 'foundations-components/components/Flex';
import { usePortalsAuth, PortalsAccessDenied } from 'portals-platform-lib';
import { useAppDispatch, useAppSelector } from '../redux';
import { selectIsAuthenticated, selectIsAuthInitialized } from '../features/AuthSlice/AuthSelectors';
import { previewLoginVerified } from '../features/AuthSlice/AuthSlice';
import { selectIsPreview, selectPreviewJwt } from '../features/PreviewSlice/PreviewSelectors';
import { PublicPortalSettingsProvider, usePublicPortalSettings } from '../hooks/usePublicPortalSettings';
import { Footer } from '../components/Footer';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AuthUserContext = /*#__PURE__*/createContext(undefined);
export const useAuthUser = () => useContext(AuthUserContext);
const spinner = /*#__PURE__*/_jsx(Flex, {
  justifyContent: "center",
  alignItems: "center",
  minBlockSize: "100%",
  children: /*#__PURE__*/_jsx(LoadingSpinner, {})
});
const SettingsGate = ({
  children
}) => {
  const {
    portalSettings,
    loading: portalSettingsLoading,
    error: portalSettingsError
  } = usePublicPortalSettings();
  const isPortalSettingsLoading = portalSettingsLoading || !portalSettings && !portalSettingsError;
  if (isPortalSettingsLoading) {
    return spinner;
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};

// Wraps authenticated content with settings provider, settings gate,
// page layout (flex column for sticky footer), and the Footer itself.
// Placed inside the auth gates so the settings query only fires post-auth.
const AuthenticatedContent = ({
  children
}) => /*#__PURE__*/_jsx(PublicPortalSettingsProvider, {
  children: /*#__PURE__*/_jsx(SettingsGate, {
    children: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      minBlockSize: "100%",
      children: [/*#__PURE__*/_jsx(Flex, {
        flexGrow: 1,
        flexDirection: "column",
        children: children
      }), /*#__PURE__*/_jsx(Footer, {})]
    })
  })
});

// Non-preview: delegates auth to the shared portals-platform-lib hook which
// handles loading state and login redirect, then renders PortalsAccessDenied
// when the contact lacks instance access.
const ProductionAuthGate = ({
  children
}) => {
  const {
    isLoading,
    isAccessDenied,
    user
  } = usePortalsAuth();
  if (isLoading) {
    return spinner;
  }
  if (isAccessDenied) {
    return /*#__PURE__*/_jsx(PortalsAccessDenied, {});
  }
  if (!user) {
    return spinner;
  }
  return /*#__PURE__*/_jsx(AuthUserContext.Provider, {
    value: user,
    children: children
  });
};

// Preview mode (Theme Editor): uses a separate CHIRP RPC
// (BuyerPreviewLoginRpc) authenticated via the JWT in the URL hash.
const PreviewAuthGate = ({
  children
}) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isAuthInitialized = useAppSelector(selectIsAuthInitialized);
  const previewJwt = useAppSelector(selectPreviewJwt);
  useEffect(() => {
    // An empty-string previewJwt will fail the preview login RPC and surface
    // as a clear auth failure, which is preferable to silently hanging in
    // the loading state if the URL was missing the JWT fragment.
    void dispatch(previewLoginVerified(previewJwt !== null && previewJwt !== void 0 ? previewJwt : ''));
  }, [dispatch, previewJwt]);
  if (!isAuthInitialized) {
    return spinner;
  }
  if (!isAuthenticated) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "APP_NOT_INITIALIZED"
    });
  }
  return /*#__PURE__*/_jsx(AuthUserContext.Provider, {
    value: undefined,
    children: children
  });
};
export const AuthWrapper = ({
  children
}) => {
  const isPreview = useAppSelector(selectIsPreview);
  const content = /*#__PURE__*/_jsx(AuthenticatedContent, {
    children: children
  });
  if (isPreview) {
    return /*#__PURE__*/_jsx(PreviewAuthGate, {
      children: content
    });
  }
  return /*#__PURE__*/_jsx(ProductionAuthGate, {
    children: content
  });
};